"use client"

import { getFixedT } from "@/languages/i18n"
import { FormInstance, InputRef, message } from "antd"
import { useRef, useState } from "react"
import { FormFieldsToUpdate, IcoData, IcoDataApiResponse, UseIcoDataReturn } from "../types"

export const useIcoData = (form: FormInstance, locale?: string): UseIcoDataReturn => {
    const t = getFixedT(locale)
    const [fetchingIco, setFetchingIco] = useState(false)
    const [icoData, setIcoData] = useState<IcoData | null>(null)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const icoInputRef = useRef<InputRef>(null)

    const getIcoValue = (): string => {
        const formValues = form.getFieldsValue()
        const formIcoValue = form.getFieldValue("ico")
        const inputElement = icoInputRef.current?.input as HTMLInputElement | undefined
        const inputIcoValue = inputElement?.value
        return (formIcoValue || formValues?.ico || inputIcoValue || "").toString().trim()
    }

    const showError = (errorMsg: string): void => {
        setErrorMessage(errorMsg)
        message.error(errorMsg)
    }

    const handleFetchIcoData = async () => {
        setErrorMessage(null)

        try {
            const icoValue = getIcoValue()

            if (!icoValue) {
                const errorMsg = t("Please enter IČO")
                showError(errorMsg)
                return
            }

            const icoRegex = /^\d{8}$/
            if (!icoRegex.test(icoValue)) {
                const errorMsg = t("IČO must be 8 digits")
                showError(errorMsg)
                return
            }

            setFetchingIco(true)

            const response = await fetch(`/api/ico-data?ico=${icoValue}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            })

            if (!response.ok) {
                const errorMsg = getErrorMessageByStatus(response.status)
                showError(errorMsg)
                setFetchingIco(false)
                return
            }

            const result = (await response.json()) as IcoDataApiResponse

            if (!result.success || !result.data) {
                const errorMsg = t("No company data found")
                showError(errorMsg)
                setFetchingIco(false)
                return
            }

            const { companyName, dic, address } = result.data

            const fieldsToUpdate: FormFieldsToUpdate = {}

            if (companyName) {
                fieldsToUpdate.companyName = companyName
            }

            if (dic) {
                fieldsToUpdate.dic = dic
            }

            if (address) {
                fieldsToUpdate.address = address
            }

            if (Object.keys(fieldsToUpdate).length > 0) {
                form.setFieldsValue(fieldsToUpdate)
                setIcoData(result.data)
                setErrorMessage(null)
                message.success(t("Company data loaded successfully"))
            } else {
                const errorMsg = t("No company data found")
                setErrorMessage(errorMsg)
                message.warning(errorMsg)
            }
        } catch (error) {
            console.error("Error fetching IČO data:", error)
            const errorMsg = error instanceof Error ? error.message : t("Unknown error")
            showError(errorMsg)
        } finally {
            setFetchingIco(false)
        }
    }

    const getErrorMessageByStatus = (status: number): string => {
        if (status === 404) {
            return t("Company not found with this IČO")
        }
        if (status === 400) {
            const icoValue = getIcoValue()
            return icoValue ? t("IČO must be 8 digits") : t("IČO is required")
        }
        if (status === 500) {
            return t("Failed to fetch IČO data")
        }
        return t("Failed to fetch IČO data")
    }

    const resetIcoData = () => {
        setIcoData(null)
        setErrorMessage(null)
    }

    const clearError = () => {
        setErrorMessage(null)
    }

    return {
        fetchingIco,
        icoData,
        handleFetchIcoData,
        icoInputRef,
        resetIcoData,
        errorMessage,
        clearError,
    }
}

